import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";

const STORAGE_KEY = "selectedClientId";

interface ClientSelectionValue {
  /** The client the user explicitly chose, or null to use the default. */
  selectedClientId: string | null;
  setSelectedClientId: (id: string | null) => void;
}

const ClientSelectionContext = createContext<ClientSelectionValue | null>(null);

/**
 * Holds the user's chosen client across the app (the `:clientId` URL param was
 * removed). Persisted to localStorage so the choice survives refresh/new tabs;
 * validation against the user's actual clients happens in `useClientId`.
 */
export function ClientSelectionProvider({ children }: { children: ReactNode }) {
  const [selectedClientId, setSelectedClientIdState] = useState<string | null>(
    () => {
      try {
        return localStorage.getItem(STORAGE_KEY);
      } catch {
        return null;
      }
    },
  );

  const setSelectedClientId = useCallback((id: string | null) => {
    setSelectedClientIdState(id);
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore storage failures (private mode, quota) — selection still works
      // for the session via state.
    }
  }, []);

  return (
    <ClientSelectionContext.Provider
      value={{ selectedClientId, setSelectedClientId }}
    >
      {children}
    </ClientSelectionContext.Provider>
  );
}

export function useClientSelection(): ClientSelectionValue {
  const ctx = useContext(ClientSelectionContext);
  if (!ctx) {
    throw new Error(
      "useClientSelection must be used within a ClientSelectionProvider",
    );
  }
  return ctx;
}
